import { CircularProgress, Grid } from "@mui/material";
import { useCallback } from "react";
import {
  useGetAssessmentQuery,
  useGetSubmissionQuery,
} from "../../../redux/Assessment/assessmentApis";
import {
  CandidateDetails,
  JobRequestDetails,
} from "../../../redux/Hire/jobRequestApi";
import AssessmentFeedback from "./AssessmentFeedback";
import AssessmentFeedbackView from "./AssessmentFeedbackView";
import AssessmentForm from "./AssessmentForm";
import AssessmentInfo from "./AssessmentInfo";

type Props = {
  candidateDetail?: CandidateDetails;
  job: JobRequestDetails;
  stage: string;
  setSelectedStage: (stage: any) => void;
};

enum AssessmentViews {
  form = "form",
  info = "info",
  feedback = "feedback",
  feedbackView = "feedbackView",
  loading = "loading",
}

const AssessmentController = ({
  job,
  setSelectedStage,
  stage,
  candidateDetail,
}: Props) => {
  const { data: submissionData, isLoading: isLoadingSubmission } =
    useGetSubmissionQuery({
      candidateId: candidateDetail?.id.toString() || "",
      jobId: job.jobId.toString(),
      stage,
    });
  const { data: assessmentData, isLoading: isLoadingAssessment } =
    useGetAssessmentQuery({
      candidateId: candidateDetail?.id.toString() || "",
      jobId: job.jobId.toString(),
      stage,
    });

  let view: AssessmentViews = AssessmentViews.form;

  if (assessmentData?.successful) view = AssessmentViews.info;

  if (submissionData?.successful && submissionData.data.submissionAt)
    view = AssessmentViews.feedback;

  if (submissionData?.successful && submissionData.data.feedBackText)
    view = AssessmentViews.feedbackView;

  if (isLoadingAssessment || isLoadingSubmission)
    view = AssessmentViews.loading;

  const render = useCallback(
    (view: AssessmentViews) => {
      if (!candidateDetail) return null;

      switch (view) {
        case AssessmentViews.form: {
          return (
            <AssessmentForm
              candidateDetail={candidateDetail}
              job={job}
              stage={stage}
              assessmentDetails={assessmentData?.data}
            />
          );
        }
        case AssessmentViews.info: {
          return <AssessmentInfo assessmentDetails={assessmentData?.data[0]} />;
        }
        case AssessmentViews.feedback: {
          return (
            <AssessmentFeedback
              assessment={submissionData?.data}
              candidateDetail={candidateDetail}
              job={job}
            />
          );
        }
        case AssessmentViews.feedbackView: {
          return <AssessmentFeedbackView assessment={submissionData?.data} />;
        }
        case AssessmentViews.loading: {
          return (
            <Grid
              container
              item
              justifyContent={"center"}
              alignItems={"center"}
            >
              <CircularProgress color="primary" size={36}></CircularProgress>
            </Grid>
          );
        }
        default:
          return (
            <AssessmentForm
              candidateDetail={candidateDetail}
              job={job}
              stage={stage}
              assessmentDetails={assessmentData?.data}
            />
          );
      }
    },
    [assessmentData?.data, candidateDetail, job, stage, submissionData?.data],
  );

  return (
    <Grid item bgcolor={"#fff"} px={1} mb={1} borderRadius={1}>
      {render(view)}
    </Grid>
  );
};

export default AssessmentController;
